/**
 * Experiment goals the API accepts, in the order the pickers list them.
 * Shared by the new-test wizard and the editor's Launch tab.
 */
export type ExperimentGoalOption = {
  id: string
  name: string
  type: "conversion" | "engagement"
  icon: string
  description: string
  explanation: string
  outcome: string
  requiresFinishPage: boolean
  popular?: boolean
}

export const EXPERIMENT_GOALS: ExperimentGoalOption[] = [
  {
    id: "form_submissions",
    name: "Form submissions",
    type: "conversion",
    icon: "Edit01Icon",
    description: "Visitors who submit a form on this page",
    explanation:
      "Use this when you want to optimize contact forms, sign-up forms, or lead generation forms. Test different form designs, field counts, button text, or placement to increase submissions.",
    outcome: "More completed forms and higher lead generation",
    requiresFinishPage: false,
    popular: true,
  },
  {
    id: "click_through_rate",
    name: "Any click on the page",
    type: "conversion",
    icon: "Cursor01Icon",
    // The backend counts every click (nav, logo, tabs), not one CTA; say so here.
    description: "Every click counts, nav and logo too. Use a form or page goal when you can",
    explanation:
      "Perfect for testing call-to-action buttons, links, navigation menus, or promotional banners. Compare different button colors, text, sizes, or placements to see which drives more clicks.",
    outcome: "Higher click rates on CTAs and improved engagement",
    requiresFinishPage: false,
    popular: true,
  },
  {
    id: "page_funnel",
    name: "Destination page",
    type: "conversion",
    icon: "Target01Icon",
    description: "Visitors who reach a page you choose, like /thank-you",
    explanation:
      "Use this to optimize multi-step processes like checkout flows, onboarding sequences, or conversion funnels. Test different page layouts or navigation paths to improve completion rates.",
    outcome: "More users completing desired journeys",
    requiresFinishPage: true,
  },
  {
    id: "external_page_funnel",
    name: "Click to another site",
    type: "conversion",
    icon: "Location01Icon",
    description: "Visitors who follow a link to a page you choose on another site",
    explanation:
      "Best used when tracking how many users are sent to an external domain or website from your site. Use this for affiliate campaigns, external partners, or sign-up flows that finish elsewhere.",
    outcome: "Higher rate of users sent to external destinations",
    requiresFinishPage: true,
  },
  {
    id: "revenue",
    name: "Payments (Stripe)",
    type: "conversion",
    icon: "Dollar01Icon",
    description: "Paid Stripe payments per visitor. Needs Stripe; trial payments land up to 14 days later",
    explanation:
      "Best for e-commerce sites or any business with direct revenue tracking. Test product pages, pricing displays, checkout processes, or upsell offers to maximize revenue per visitor.",
    outcome: "Higher revenue per visitor and increased sales",
    requiresFinishPage: false,
  },
  {
    id: "session_time",
    name: "Visit time",
    type: "engagement",
    icon: "Clock01Icon",
    description: "Time on the page. It does not show who bought",
    explanation:
      "Use this when you want people to spend more time engaging with your content. Test different layouts, video placements, or interactive elements to keep visitors longer.",
    outcome: "Longer visits and deeper engagement",
    requiresFinishPage: false,
  },
  {
    id: "bounce_rate",
    name: "Bounce rate",
    type: "engagement",
    icon: "ArrowUp01Icon",
    description: "Visitors who leave without seeing a second page",
    explanation:
      "Ideal for landing pages or blog posts where you want visitors to explore more of your site. Test headlines, content structure, or navigation elements to encourage users to visit additional pages.",
    outcome: "Lower bounce rates and more pages viewed",
    requiresFinishPage: false,
  },
];

export function findExperimentGoal(id: string | null | undefined): ExperimentGoalOption | undefined {
  return EXPERIMENT_GOALS.find((goal) => goal.id === id)
}
